import { HttpGateway } from '@mojility/coach-core'

export default class CoachGateway extends HttpGateway {

  constructor(coachHttpClientFactory, eventsService) {
    super(coachHttpClientFactory);
    this.eventsService = eventsService;
  }

  requestLogin(username, password) {
    return this.post('authenticate', {
      email: username,
      password: password
    }, {
      401: () => Failure.reject(Severity.WARNING, 'Invalid email or password.')
    });
  }

  updateOrganization(secretKey, data) {
    return this.authenticatedPut(secretKey, 'organization', data);
  }

  deleteOrganization(secretKey, organizationId) {
    return this.authenticatedDelete(secretKey, `organization/${organizationId}`);
  }

  getDataForCoachInterface(secretKey) {
    return this.authenticatedGet(secretKey, 'data_for_coach_interface');
  }

  createNewQuestionnaire(secretKey) {
    return this.authenticatedPost(secretKey, 'questionnaire', {});
  }

  sendReminderToPerson(secretKey, data) {
    return this.authenticatedPost(secretKey, 'questionnaire_instance/remind_person', data);
  }

  sendInvestigationReminder(secretKey, message, investigation) {
    return this.authenticatedPost(secretKey, `investigation/${investigation.id}/remind`, {message: message});
  }

  sendQuestionnaireToPerson(secretKey, data) {
    return this.authenticatedPost(secretKey, 'person/email', data);
  }

  sendQuestionnaireToWorkspace(secretKey, data) {
    return this.authenticatedPost(secretKey, `workspace/${data.workspace_id}/investigation`, data);
  }

  updateWorkspace(secretKey, data) {
    return this.authenticatedPut(secretKey, 'workspace', data);
  }

  createWorkspace(secretKey, newWorkspace) {
    return this.authenticatedPost(secretKey, 'workspace', newWorkspace);
  }

  createWorkspaceWithOrganization(secretKey, newWorkspace) {
    return this.authenticatedPost(secretKey, 'organization/workspaces', newWorkspace);
  }

  archiveWorkspace(secretKey, workspaceId) {
    return this.authenticatedDelete(secretKey, `workspace/${workspaceId}`);
  }

  deleteQuestionnaireInstance(secretKey, instanceId) {
    return this.authenticatedDelete(secretKey, `questionnaire_instance/${instanceId}`);
  }

  deleteQuestionnaire(secretKey, questionnaireId) {
    return this.authenticatedDelete(secretKey, `questionnaire/${questionnaireId}`);
  }

  invitePersonToBeCoached(secretKey, inviteeEmail) {
    return this.authenticatedPost(secretKey, 'invitation', {email: inviteeEmail}, {
      400: response => response.json().then(data => this.eventsService.publishSystemError(data.message))
    });
  }

  getSentInvitations(secretKey) {
    return this.authenticatedGet(secretKey, 'invitation/sent');
  }

  getReceivedInvitations(secretKey) {
    return this.authenticatedGet(secretKey, `invitation/received`);
  }

  acceptInvitation(secretKey, invitationId) {
    return this.authenticatedPut(secretKey, 'invitation', {invitation_id: invitationId});
  }

  deleteInvitation(secretKey, invitationId) {
    return this.authenticatedDelete(secretKey, `invitation/${invitationId}`);
  }

  dischargeCoachee(secretKey, coacheeId) {
    return this.authenticatedDelete(secretKey, `person/${coacheeId}`);
  }

  getDefaultBranding(secretKey) {
    return this.authenticatedGet(secretKey, 'coach/branding');
  }

  updateBranding(secretKey, branding) {
    return this.authenticatedPut(secretKey, 'coach/branding', {
      css: branding.css,
      accent_colour: branding.accent_colour,
      secondary_colour: branding.secondary_colour,
      font_name: branding.font_name,
      domain_name: branding.domain_name,
      site_name: branding.site_name,
      title: branding.title,
      description: branding.description
    });
  }

  getCurrentLogoUrl(secretKey) {
    return this.authenticatedGet(secretKey, 'coach/branding/logo');
  }

  getCurrentStoreImageUrl(secretKey) {
    return this.authenticatedGet(secretKey, 'coach/branding/store_image');
  }

  getQuestionnaireInstancesForPerson(secretKey, personId) {
    return this.authenticatedGet(secretKey, `coach/person/${personId}/questionnaire_instances`);
  }

  duplicateQuestionnaire(secretKey, questionnaireId) {
    return this.authenticatedPost(secretKey, `questionnaire/${questionnaireId}`, {});
  }

  setQuestionnaireEcommerceSettings(secretKey, questionnaireId, newPrice, publiclyAvailable, newDescription) {
    return this.authenticatedPut(secretKey, `questionnaire/${questionnaireId}/ecommerce`, {
      price: newPrice,
      publicly_available: publiclyAvailable,
      short_description: newDescription
    });
  }

  investigationsOfWorkspace(secretKey, workspaceId) {
    return this.authenticatedGet(secretKey, `workspace/${workspaceId}/investigation`);
  }

  lockInvestigation(secretKey, investigationId) {
    return this.authenticatedPut(secretKey, `investigation/${investigationId}`, {});
  }

  setReportAvailability(secretKey, investigationId, availability) {
    return this.authenticatedPut(secretKey, `investigation/${investigationId}/reports`, {
      availability: availability
    });
  }

  getInvestigation(secretKey, id) {
    return this.authenticatedGet(secretKey, `investigation/${id}`);
  }

  archiveInvestigation(secretKey, id) {
    return this.authenticatedDelete(secretKey, `investigation/${id}`);
  }

  updateQuestionnaire(secretKey, questionnaireId, personId) {
    return this.authenticatedPut(secretKey, `questionnaire/${questionnaireId}`, {person_id: personId});
  }

  getCoaches(secretKey) {
    return this.authenticatedGet(secretKey, 'client/coaches');
  }

  dischargeCoach(secretKey, coachId) {
    return this.authenticatedDelete(secretKey, `client/person/${coachId}`);
  }

  getReceivedInvestigations (secretKey) {
    return this.authenticatedGet(secretKey, 'client/questionnaire_instances');
  }
}
